import { FC } from 'react';
import classNames from 'classnames';
import {
  CheckmarkFilled,
  CircleDash,
  InProgress,
  Misuse,
  SkipForwardFilled,
  PauseFilled,
} from '@carbon/react/icons';
import { PlainLink } from 'components/link';
import type { InventoryAutomationStatus, RecomputeState } from './types';
import styles from './page.module.scss';

const EM_DASH = '—';

export const StateCell: FC<{ state: RecomputeState }> = ({ state }) => {
  switch (state) {
    case 'completed':
      return (
        <span className={classNames(styles.stateBadge, styles.stateCompleted)}>
          <CheckmarkFilled />
          Completed
        </span>
      );
    case 'running':
      return (
        <span className={classNames(styles.stateBadge, styles.stateRunning)}>
          <InProgress />
          Running
        </span>
      );
    case 'held':
      return (
        <span className={classNames(styles.stateBadge, styles.stateHeld)}>
          <PauseFilled />
          Held
        </span>
      );
    case 'failed':
      return (
        <span className={classNames(styles.stateBadge, styles.stateFailed)}>
          <Misuse />
          Failed
        </span>
      );
    case 'skipped':
      return (
        <span className={classNames(styles.stateBadge, styles.stateSkipped)}>
          <SkipForwardFilled />
          Skipped
        </span>
      );
    default:
      return (
        <span className={classNames(styles.stateBadge, styles.statePending)}>
          <CircleDash />
          Pending
        </span>
      );
  }
};

// A workspace the row names. The label is the workspace's own name when it has one, falling back
// to the id so a reader can still act on a workspace that has since been renamed away.
export const WorkspaceCell: FC<{
  id: number | null;
  name: string | null;
  href: string | null;
}> = ({ id, name, href }) => {
  if (!id) return <>{EM_DASH}</>;

  const label = name || `#${id}`;
  return href ? (
    <PlainLink href={href} className={styles.workspaceLink}>
      {label}
    </PlainLink>
  ) : (
    <>{label}</>
  );
};

// The batch, broken down. The row's own state is one word for the whole run, which is honest but
// coarse: a run reading "running" can already have rebuilt twenty units and skipped three.
//
// Nothing is shown for a run covering every unit in its area, because there is no fixed list to
// count against until the generation request reports.
export const OutcomeSummary: FC<{ run: InventoryAutomationStatus }> = ({
  run,
}) => {
  if (run.allUnits) return <>{EM_DASH}</>;

  const counts = run.outcomeCounts;
  if (!counts) return <>{EM_DASH}</>;

  const parts: string[] = [];
  if (counts.completed) parts.push(`${counts.completed} rebuilt`);
  if (counts.skipped) parts.push(`${counts.skipped} skipped`);
  if (counts.failed) parts.push(`${counts.failed} failed`);
  if (counts.queued) parts.push(`${counts.queued} queued`);
  if (counts.inProgress) parts.push(`${counts.inProgress} in progress`);
  if (counts.notRun) parts.push(`${counts.notRun} not run`);

  if (parts.length === 0) return <>{EM_DASH}</>;

  return (
    <span
      className={classNames(styles.outcomeSummary, {
        [styles.outcomeSummaryBad]: counts.failed > 0,
      })}
    >
      {parts.join(' · ')}
    </span>
  );
};
